import React, { useEffect, useMemo, useState } from "react";
import {
    collection,
    doc,
    getDoc,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    updateDoc,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadString } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { db } from "../utils/FirebaseConfig";
import { compressImageFile } from "../utils/compressImage";
import "../styles/Kempen.css";

const storage = getStorage();

const getUserProfile = () => {
    try {
        return JSON.parse(localStorage.getItem("userProfile") || "{}");
    } catch {
        return {};
    }
};

const ymd = (d) => {
    const x = new Date(d);
    const yyyy = x.getFullYear();
    const mm = String(x.getMonth() + 1).padStart(2, "0");
    const dd = String(x.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const msDateTime = (ms) => {
    if (!ms) return "-";
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("ms-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const fmtRM = (n) => {
    const x = Number(n || 0);
    return `RM${x.toLocaleString("ms-MY")}`;
};

const clampDigits = (v) => String(v ?? "").replace(/[^\d]/g, "");

const buildHariArray = (startDateStr, days, sasaranPerHari) => {
    const start = new Date(startDateStr);
    const rows = [];
    for (let i = 0; i < days; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        rows.push({
            date: ymd(d),
            sasaran: Number(sasaranPerHari || 0),
            terkumpul: 0,
        });
    }
    return rows;
};

export default function Kempen() {
    const userProfile = getUserProfile();
    const mosqueId = userProfile?.mosqueId || null;

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 900);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const [loadingKempen, setLoadingKempen] = useState(true);
    const [kempenList, setKempenList] = useState([]);

    const [loadingDerma, setLoadingDerma] = useState(true);
    const [dermaRekod, setDermaRekod] = useState([]);

    // ===== ToyyibPay (config)
    const [showSettings, setShowSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [toyyibCategoryCode, setToyyibCategoryCode] = useState("");
    const [toyyibSecretKey, setToyyibSecretKey] = useState("");

    // ===== UI: form panel
    const [showForm, setShowForm] = useState(false);

    // ===== form kempen
    const [editingId, setEditingId] = useState(null); // edit existing
    const [draftId, setDraftId] = useState(null); // new draft (image)
    const [tajuk, setTajuk] = useState("");
    const [keterangan, setKeterangan] = useState("");
    const [aktif, setAktif] = useState(true);

    const [sasaran, setSasaran] = useState("");
    const [modHarian, setModHarian] = useState(false);
    const [startDate, setStartDate] = useState(ymd(new Date()));
    const [days, setDays] = useState(30);
    const [sasaranPerHari, setSasaranPerHari] = useState(2000);

    const [imejUrl, setImejUrl] = useState("");
    const [uploading, setUploading] = useState(false);

    const docRef = useMemo(() => {
        if (!mosqueId) return null;
        return doc(db, "masajid", mosqueId);
    }, [mosqueId]);

    const [featuredId, setFeaturedId] = useState(null);

    // ===== subscribe masjid doc (kempen + toyyib)
    useEffect(() => {
        if (!docRef) return;

        const unsub = onSnapshot(
            docRef,
            (snap) => {
                const data = snap.data() || {};
                const kempenNode = data?.kempen || {};
                setFeaturedId(kempenNode?.featuredId || null);

                setKempenList(Array.isArray(kempenNode?.senarai) ? kempenNode.senarai : []);

                const t = kempenNode?.toyyib || {};
                setToyyibCategoryCode(t.categoryCode || "");
                setToyyibSecretKey(t.secretKey || "");

                setLoadingKempen(false);
            },
            (err) => {
                console.error("❌ Kempen snapshot error:", err);
                setLoadingKempen(false);
            }
        );

        return () => unsub();
    }, [docRef]);

    // ===== derma rekod
    useEffect(() => {
        if (!mosqueId) return;

        const qRef = query(
            collection(db, "masajid", mosqueId, "derma_rekod"),
            orderBy("createdAt", "desc"),
            limit(80)
        );

        const unsub = onSnapshot(
            qRef,
            (snap) => {
                setDermaRekod(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
                setLoadingDerma(false);
            },
            (err) => {
                console.error("❌ Derma rekod snapshot error:", err);
                setLoadingDerma(false);
            }
        );

        return () => unsub();
    }, [mosqueId]);

    const ensureDocExists = async () => {
        if (!docRef) return;

        const snap = await getDoc(docRef);

        // ✅ Kalau doc belum wujud sahaja, create skeleton
        if (!snap.exists()) {
            await setDoc(
                docRef,
                {
                    kempen: {
                        toyyib: { categoryCode: "", secretKey: "" },
                        featuredId: null,
                        senarai: [],
                    },
                },
                { merge: true }
            );
        } else {
            // ✅ doc dah wujud → jangan overwrite senarai!
            // Optional: pastikan node kempen/toyyib wujud tanpa kacau senarai
            await setDoc(
                docRef,
                {
                    kempen: {
                        toyyib: { categoryCode: "", secretKey: "" },
                        featuredId: null,
                    },
                },
                { merge: true }
            );
        }
    };


    const saveSettings = async () => {
        if (!docRef) return;
        try {
            setSavingSettings(true);
            await ensureDocExists();

            await updateDoc(docRef, {
                "kempen.toyyib.categoryCode": (toyyibCategoryCode || "").trim(),
                "kempen.toyyib.secretKey": (toyyibSecretKey || "").trim(),
                "kempen.updatedAt": Date.now(),
            });

            setShowSettings(false);
        } catch (e) {
            console.error("❌ saveSettings error:", e);
            alert("Gagal simpan tetapan ToyyibPay.");
        } finally {
            setSavingSettings(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setDraftId(null);
        setTajuk("");
        setKeterangan("");
        setAktif(true);
        setSasaran("");
        setModHarian(false);
        setStartDate(ymd(new Date()));
        setDays(30);
        setSasaranPerHari(2000);
        setImejUrl("");
    };

    const closeForm = () => {
        resetForm();
        setShowForm(false);
    };

    const openAddForm = () => {
        resetForm();
        setShowForm(true);
    };

    const openEditForm = (k) => {
        setShowForm(true);
        setEditingId(k.id);
        setDraftId(null);
        setTajuk(k.tajuk || "");
        setKeterangan(k.keterangan || "");
        setAktif(k.aktif !== false);
        setSasaran(k.sasaran ?? "");
        setModHarian(!!k.modHarian);

        const firstDate = k.hari?.[0]?.date;
        setStartDate(firstDate || ymd(new Date()));

        const len = Array.isArray(k.hari) ? k.hari.length : 30;
        setDays(len);

        const spd = k.hari?.[0]?.sasaran ?? 2000;
        setSasaranPerHari(spd);

        setImejUrl(k.imejUrl || "");
    };

    const saveKempen = async () => {
        if (!docRef) return;

        const t = tajuk.trim();
        if (!t) return alert("Sila isi tajuk kempen.");

        await ensureDocExists();

        const isEdit = !!editingId;
        const id = isEdit ? editingId : (draftId || uuidv4());

        const obj = {
            id,
            tajuk: t,
            keterangan: (keterangan || "").trim(),
            aktif: !!aktif,
            sasaran: sasaran === "" ? null : Number(sasaran || 0),
            modHarian: !!modHarian,
            hari: modHarian ? buildHariArray(startDate, Number(days || 30), Number(sasaranPerHari || 0)) : [],
            imejUrl: imejUrl || "",
            updatedAt: Date.now(),
            ...(isEdit ? {} : { createdAt: Date.now() }),
        };

        const next = isEdit
            ? kempenList.map((x) => (x.id === id ? { ...x, ...obj } : x))
            : [...kempenList, obj];

        await updateDoc(docRef, { "kempen.senarai": next });

        // ✅ lepas berjaya: auto close form
        closeForm();
    };

    const deleteKempen = async (id) => {
        if (!docRef) return;
        if (!window.confirm("Padam kempen ini?")) return;

        const nextList = kempenList.filter((x) => x.id !== id);
        const nextFeatured = featuredId === id ? null : featuredId;

        await updateDoc(docRef, {
            "kempen.senarai": nextList,
            "kempen.featuredId": nextFeatured,
            "kempen.updatedAt": Date.now(),
        });

        if (editingId === id) closeForm(); // atau resetForm+hideForm ikut code kau
    };

    const toggleActive = async (id) => {
        if (!docRef) return;
        const next = kempenList.map((x) =>
            x.id === id ? { ...x, aktif: !x.aktif, updatedAt: Date.now() } : x
        );
        await updateDoc(docRef, { "kempen.senarai": next });
    };

    const onPickImage = async (file) => {
        if (!docRef) return;
        if (!file) return;

        try {
            setUploading(true);

            const compressedDataUrl = await compressImageFile(file, {
                maxWidth: 1280,
                maxSizeKB: 220,
            });

            const idForImage = editingId || draftId || uuidv4();
            if (!editingId && !draftId) setDraftId(idForImage);

            const path = `kempen/${mosqueId}/${idForImage}.jpg`;
            const storageRef = ref(storage, path);

            await uploadString(storageRef, compressedDataUrl, "data_url");
            const url = await getDownloadURL(storageRef);

            setImejUrl(url);
        } catch (e) {
            console.error("❌ Upload imej error:", e);
            alert("Gagal muat naik imej.");
        } finally {
            setUploading(false);
        }
    };

    // ===== progress by kempenId (rekod berjaya sahaja)
    const jumlahByKempen = useMemo(() => {
        const map = new Map();
        (dermaRekod || []).forEach((r) => {
            const kempenId = r.kempenId || r.campaignId || null;
            if (!kempenId) return;

            const amaun = Number(r.amaun ?? r.amount ?? 0) || 0;
            const status = String(r.status || "").toLowerCase();
            const ok = status === "berjaya" || status === "paid" || status === "success";
            if (!ok) return;

            map.set(kempenId, (map.get(kempenId) || 0) + amaun);
        });
        return map;
    }, [dermaRekod]);

    const progressFor = (k) => {
        const terkumpul = Number(jumlahByKempen.get(k.id) || 0) || 0;
        const sas = Number(k?.sasaran || 0) || 0;
        if (!sas) return { percent: 0, text: fmtRM(terkumpul), terkumpul, sas: 0 };
        const percent = Math.max(0, Math.min(100, Math.round((terkumpul / sas) * 100)));
        return { percent, text: `${fmtRM(terkumpul)} / ${fmtRM(sas)}`, terkumpul, sas };
    };

    const setFeaturedKempen = async (id) => {
        if (!docRef) return;
        try {
            await ensureDocExists();
            await updateDoc(docRef, {
                "kempen.featuredId": id || null,
                "kempen.updatedAt": Date.now(),
            });
        } catch (e) {
            console.error("❌ setFeaturedKempen error:", e);
            alert("Gagal set featured kempen.");
        }
    };


    if (!mosqueId) {
        return (
            <div className="kp-page">
                <div className="pv2-block">
                    <div className="field-container">
                        <label className="field-label">Kempen</label>
                        <div className="field-value">Tiada mosqueId. Sila login semula.</div>
                    </div>
                </div>
            </div>
        );
    }

    const sortedKempen = kempenList
        .slice()
        .sort((a, b) => {
            const aF = a.id === featuredId ? 1 : 0;
            const bF = b.id === featuredId ? 1 : 0;
            if (aF !== bF) return bF - aF; // featured on top
            return (b.updatedAt || 0) - (a.updatedAt || 0);
        });


    return (
        <div className="kp-page">
            {/* Header */}
            <div className="kp-header">
                <div className="kp-headerText">
                    <h2 className="kp-title">Kempen</h2>
                    <div className="kp-subtitle">Urus kempen dan lihat rekod derma terkini.</div>
                </div>

                <div className="kp-headerActions">
                    <button className="btn-secondary kp-btn" onClick={() => setShowSettings(true)}>
                        Tetapan Bayaran
                    </button>

                    <button className="btn-primary kp-btn" onClick={openAddForm}>
                        Tambah Kempen
                    </button>
                </div>
            </div>

            {/* Layout */}
            <div className={`kp-layout ${isMobile ? "kp-mobile" : "kp-desktop"} ${showForm ? "kp-hasForm" : ""}`}>
                {/* LEFT: list + donation table */}
                {(!isMobile || !showForm) && (
                    <div className="kp-left">
                        <div className="pv2-block kp-block">


                            {loadingKempen ? (
                                <div className="field-value">Loading...</div>
                            ) : sortedKempen.length === 0 ? (
                                <div className="field-value">Belum ada kempen.</div>
                            ) : (
                                <div className="kp-list">
                                    {sortedKempen.map((k) => {
                                        const prog = progressFor(k);

                                        return (
                                            <div className="kp-card" key={k.id}>
                                                <div className="kp-cardTop">
                                                    <div className="kp-cardLeft">
                                                        <div className="kp-titleRow">
                                                            <div className="kp-name">{k.tajuk}</div>
                                                            <div className="kp-badges">
                                                                {k.id === featuredId && <span className="kp-badge featured">Utama</span>}

                                                                <span className={`kp-badge ${k.aktif ? "on" : "off"}`}>
                                                                    {k.aktif ? "Aktif" : "Tidak aktif"}
                                                                </span>
                                                            </div>

                                                        </div>

                                                        {k.keterangan ? <div className="kp-desc">{k.keterangan}</div> : null}

                                                        <div className="kp-meta">
                                                            <div className="kp-metaItem">
                                                                <div className="kp-metaLabel">Sasaran</div>
                                                                <div className="kp-metaValue">{k?.sasaran ? fmtRM(k.sasaran) : "-"}</div>
                                                            </div>

                                                            <div className="kp-metaItem">
                                                                <div className="kp-metaLabel">Dikemaskini</div>
                                                                <div className="kp-metaValue">{msDateTime(k.updatedAt)}</div>
                                                            </div>
                                                        </div>

                                                        <div className="kp-progress">
                                                            <div className="kp-progressText">
                                                                {k?.sasaran ? prog.text : fmtRM(prog.terkumpul)}
                                                            </div>

                                                            <div className="kp-progressRow">
                                                                <progress className="kp-progressBar" value={k?.sasaran ? prog.percent : 0} max={100} />
                                                                <div className="kp-percent">{k?.sasaran ? `${prog.percent}%` : "—"}</div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {k.imejUrl ? (
                                                        <div className="kp-cardRight">
                                                            <img className="kp-cover" src={k.imejUrl} alt="imej kempen" />
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="kp-btnRow">
                                                    <button className="btn-secondary" onClick={() => openEditForm(k)}>Edit</button>
                                                    <button className="btn-secondary" onClick={() => toggleActive(k.id)}>
                                                        {k.aktif ? "Nyahaktif" : "Aktifkan"}
                                                    </button>
                                                    <button className="btn-secondary kp-danger" onClick={() => deleteKempen(k.id)}>
                                                        Padam
                                                    </button>
                                                    {k.id === featuredId ? (
                                                        <button className="btn-secondary kp-featuredOn" disabled>
                                                            Featured
                                                        </button>
                                                    ) : (
                                                        <button className="btn-secondary kp-featureBtn" onClick={() => setFeaturedKempen(k.id)}>
                                                            Jadikan Utama
                                                        </button>
                                                    )}
                                                    {k.id === featuredId && (
                                                        <button className="btn-secondary kp-featureBtn" onClick={() => setFeaturedKempen(null)}>
                                                            Buang Utama
                                                        </button>
                                                    )}


                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="pv2-block kp-block">
                            <div className="kp-headRow">
                                <h3 className="kp-h3">Senarai Derma Terkini</h3>
                                <div className="kp-mini">Data: masajid/{mosqueId}/derma_rekod</div>
                            </div>

                            {loadingDerma ? (
                                <div className="field-value">Loading...</div>
                            ) : dermaRekod.length === 0 ? (
                                <div className="field-value">Belum ada rekod derma.</div>
                            ) : (
                                <div className="kp-tableWrap">
                                    <table className="kp-table">
                                        <thead>
                                            <tr>
                                                <th>Tarikh</th>
                                                <th>Nama</th>
                                                <th>Kempen</th>
                                                <th className="kp-right">Amaun</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dermaRekod.map((r) => {
                                                const kempenId = r.kempenId || r.campaignId || "";
                                                const kempen = kempenList.find((k) => k.id === kempenId);
                                                const kempenName = kempen?.tajuk || r.kempenTajuk || r.campaignTitle || "-";
                                                const amaun = Number(r.amaun ?? r.amount ?? 0) || 0;
                                                const status = r.status || "-";
                                                const nama = r.nama || r.name || "-";

                                                return (
                                                    <tr key={r.id}>
                                                        <td>{msDateTime(r.createdAt)}</td>
                                                        <td className="kp-tdName">{nama}</td>
                                                        <td className="kp-wide">{kempenName}</td>
                                                        <td className="kp-right">{fmtRM(amaun)}</td>
                                                        <td>
                                                            <span className={`kp-pill ${String(status).toLowerCase()}`}>{status}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    <div className="kp-footnote">
                                        Untuk progress % ikut kempen: rekod perlu simpan <b>kempenId</b> + <b>amaun</b> + <b>status</b>.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* RIGHT: form (desktop) */}
                {!isMobile && showForm && (
                    <div className="kp-right">
                        <div className="pv2-block kp-block kp-formBlock">
                            <div className="kp-formHeader">
                                <div>
                                    <h3 className="kp-h3">{editingId ? "Kemaskini Kempen" : "Tambah Kempen"}</h3>
                                    <div className="kp-mini">Kempen disimpan: masajid/{mosqueId} → kempen.senarai</div>
                                </div>

                                <button className="kp-x" onClick={closeForm} aria-label="Tutup borang">
                                    ✕
                                </button>
                            </div>

                            <div className="field-container">
                                <label className="field-label">Tajuk</label>
                                <input
                                    className="field-input"
                                    value={tajuk}
                                    onChange={(e) => setTajuk(e.target.value)}
                                    placeholder="contoh: Tajaan Buka Puasa"
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Sasaran (RM) (optional)</label>
                                <input
                                    className="field-input"
                                    value={sasaran}
                                    onChange={(e) => setSasaran(clampDigits(e.target.value))}
                                    placeholder="contoh: 60000"
                                    inputMode="numeric"
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Keterangan</label>
                                <textarea
                                    className="field-input"
                                    value={keterangan}
                                    onChange={(e) => setKeterangan(e.target.value)}
                                    placeholder="Ringkas & jelas..."
                                    rows={3}
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Status</label>
                                <div className="kp-row">
                                    <label className="kp-check">
                                        <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
                                        <span>Aktif</span>
                                    </label>

                                    <label className="kp-check">
                                        <input type="checkbox" checked={modHarian} onChange={(e) => setModHarian(e.target.checked)} />
                                        <span>Mod harian (contoh Ramadan)</span>
                                    </label>
                                </div>
                            </div>

                            {modHarian && (
                                <>
                                    <div className="field-container">
                                        <label className="field-label">Tarikh mula</label>
                                        <input
                                            className="field-input"
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="kp-grid-2">
                                        <div className="field-container">
                                            <label className="field-label">Bilangan hari</label>
                                            <input
                                                className="field-input"
                                                value={days}
                                                onChange={(e) => setDays(Number(clampDigits(e.target.value)) || 0)}
                                                inputMode="numeric"
                                            />
                                        </div>

                                        <div className="field-container">
                                            <label className="field-label">Sasaran per hari (RM)</label>
                                            <input
                                                className="field-input"
                                                value={sasaranPerHari}
                                                onChange={(e) => setSasaranPerHari(Number(clampDigits(e.target.value)) || 0)}
                                                inputMode="numeric"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="field-container">
                                <label className="field-label">Imej kempen (optional)</label>
                                <input
                                    className="field-input kp-file"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => onPickImage(e.target.files?.[0])}
                                    disabled={uploading}
                                />
                                <div className="kp-note">
                                    {uploading ? "Sedang muat naik (imej dikompres)..." : "Imej akan dikecilkan sebelum upload."}
                                </div>

                                {imejUrl && (
                                    <div className="kp-thumbWrap">
                                        <img className="kp-thumb" src={imejUrl} alt="preview" />
                                    </div>
                                )}
                            </div>

                            <div className="kp-actions">
                                <button className="btn-primary" onClick={saveKempen} disabled={uploading}>
                                    {editingId ? "Kemaskini" : "Tambah"}
                                </button>
                                <button className="btn-secondary" onClick={closeForm} disabled={uploading}>
                                    Tutup
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MOBILE: form fullscreen */}
                {isMobile && showForm && (
                    <div className="kp-mobileForm">
                        <div className="pv2-block kp-block kp-formBlock">
                            <div className="kp-formHeader">
                                <div>
                                    <h3 className="kp-h3">{editingId ? "Kemaskini Kempen" : "Tambah Kempen"}</h3>
                                    <div className="kp-mini">Borang penuh skrin (mobile)</div>
                                </div>

                                <button className="kp-x" onClick={closeForm} aria-label="Tutup borang">
                                    ✕
                                </button>
                            </div>

                            <div className="field-container">
                                <label className="field-label">Tajuk</label>
                                <input
                                    className="field-input"
                                    value={tajuk}
                                    onChange={(e) => setTajuk(e.target.value)}
                                    placeholder="contoh: Tajaan Buka Puasa"
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Sasaran (RM) (optional)</label>
                                <input
                                    className="field-input"
                                    value={sasaran}
                                    onChange={(e) => setSasaran(clampDigits(e.target.value))}
                                    placeholder="contoh: 60000"
                                    inputMode="numeric"
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Keterangan</label>
                                <textarea
                                    className="field-input"
                                    value={keterangan}
                                    onChange={(e) => setKeterangan(e.target.value)}
                                    placeholder="Ringkas & jelas..."
                                    rows={3}
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">Status</label>
                                <div className="kp-row">
                                    <label className="kp-check">
                                        <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
                                        <span>Aktif</span>
                                    </label>

                                    <label className="kp-check">
                                        <input type="checkbox" checked={modHarian} onChange={(e) => setModHarian(e.target.checked)} />
                                        <span>Mod harian (contoh Ramadan)</span>
                                    </label>
                                </div>
                            </div>

                            {modHarian && (
                                <>
                                    <div className="field-container">
                                        <label className="field-label">Tarikh mula</label>
                                        <input
                                            className="field-input"
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="kp-grid-2">
                                        <div className="field-container">
                                            <label className="field-label">Bilangan hari</label>
                                            <input
                                                className="field-input"
                                                value={days}
                                                onChange={(e) => setDays(Number(clampDigits(e.target.value)) || 0)}
                                                inputMode="numeric"
                                            />
                                        </div>

                                        <div className="field-container">
                                            <label className="field-label">Sasaran per hari (RM)</label>
                                            <input
                                                className="field-input"
                                                value={sasaranPerHari}
                                                onChange={(e) => setSasaranPerHari(Number(clampDigits(e.target.value)) || 0)}
                                                inputMode="numeric"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="field-container">
                                <label className="field-label">Imej kempen (optional)</label>
                                <input
                                    className="field-input kp-file"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => onPickImage(e.target.files?.[0])}
                                    disabled={uploading}
                                />
                                <div className="kp-note">
                                    {uploading ? "Sedang muat naik (imej dikompres)..." : "Imej akan dikecilkan sebelum upload."}
                                </div>

                                {imejUrl && (
                                    <div className="kp-thumbWrap">
                                        <img className="kp-thumb" src={imejUrl} alt="preview" />
                                    </div>
                                )}
                            </div>

                            <div className="kp-actions">
                                <button className="btn-primary" onClick={saveKempen} disabled={uploading}>
                                    {editingId ? "Kemaskini" : "Tambah"}
                                </button>
                                <button className="btn-secondary" onClick={closeForm} disabled={uploading}>
                                    Tutup
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Settings Drawer */}
            {showSettings && (
                <div className="kp-overlay" role="dialog" aria-modal="true">
                    <div className="kp-backdrop" onClick={() => setShowSettings(false)} />

                    <div className="kp-drawer">
                        <div className="kp-drawerHeader">
                            <div>
                                <div className="kp-drawerTitle">Tetapan Bayaran (ToyyibPay)</div>
                                <div className="kp-mini">Disimpan: masajid/{mosqueId} → kempen.toyyib</div>
                            </div>

                            <button className="kp-x" onClick={() => setShowSettings(false)} aria-label="Tutup tetapan">
                                ✕
                            </button>
                        </div>

                        <div className="kp-drawerBody">
                            <div className="field-container">
                                <label className="field-label">ToyyibPay Category Code</label>
                                <input
                                    className="field-input"
                                    value={toyyibCategoryCode}
                                    onChange={(e) => setToyyibCategoryCode(e.target.value)}
                                    placeholder="contoh: j0tzqhka"
                                    disabled={savingSettings}
                                />
                            </div>

                            <div className="field-container">
                                <label className="field-label">ToyyibPay Secret Key</label>
                                <input
                                    className="field-input"
                                    type="password"
                                    value={toyyibSecretKey}
                                    onChange={(e) => setToyyibSecretKey(e.target.value)}
                                    placeholder="paste secret key"
                                    disabled={savingSettings}
                                />
                            </div>

                            <div className="kp-actions">
                                <button className="btn-primary" onClick={saveSettings} disabled={savingSettings}>
                                    {savingSettings ? "Menyimpan..." : "Simpan Tetapan"}
                                </button>
                                <button className="btn-secondary" onClick={() => setShowSettings(false)} disabled={savingSettings}>
                                    Batal
                                </button>
                            </div>

                            <div className="kp-help">
                                Tip: Tetapan bayaran jarang diubah, jadi kita letak dalam drawer supaya urus kempen lebih “straight”.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
